import type { useUser } from "#hooks/use-auth"
import type { useUpdateTourStatus } from "#hooks/use-tour-status"
import { useTour } from "@reactour/tour"
import React from "react"

interface TourProgressTrackerProps {
  isTourCompleted: boolean
  user: ReturnType<typeof useUser>["data"]
  updateTourStatus: ReturnType<typeof useUpdateTourStatus>
  totalSteps: number
  needsOnboarding: boolean
  skipRef?: React.MutableRefObject<boolean>
}

export function TourProgressTracker({
  isTourCompleted,
  user,
  updateTourStatus,
  totalSteps,
  needsOnboarding,
  skipRef: externalSkipRef,
}: TourProgressTrackerProps) {
  const { currentStep, isOpen } = useTour()
  const prevStepRef = React.useRef<number | null>(null)
  const wasOpenRef = React.useRef<boolean>(false)
  const internalSkipRef = React.useRef<boolean>(false)
  const skipRef = externalSkipRef || internalSkipRef
  const prevIsCompletedRef = React.useRef<boolean>(isTourCompleted)
  const isInitializingRef = React.useRef<boolean>(false)

  React.useEffect(() => {
    if (prevIsCompletedRef.current && !isTourCompleted) {
      prevStepRef.current = null
      wasOpenRef.current = false
      skipRef.current = false
      isInitializingRef.current = true
    }
    prevIsCompletedRef.current = isTourCompleted
  }, [isTourCompleted, skipRef])

  React.useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      isInitializingRef.current = false
      skipRef.current = false
      if (currentStep !== undefined) {
        prevStepRef.current = currentStep
      }
    }
  }, [isOpen, currentStep, skipRef])

  const mutationInProgressRef = React.useRef<boolean>(false)

  React.useEffect(() => {
    if (
      !isTourCompleted &&
      isOpen &&
      !isInitializingRef.current &&
      !mutationInProgressRef.current &&
      currentStep !== undefined &&
      prevStepRef.current !== null &&
      prevStepRef.current !== currentStep
    ) {
      const now = new Date().toISOString()
      const isLastStep = currentStep >= totalSteps - 1
      const isSetupStep = currentStep === 0 && needsOnboarding

      if (!isSetupStep) {
        mutationInProgressRef.current = true
        const tourStartedAt = user?.tourStartedAt ? new Date(user.tourStartedAt).toISOString() : now

        updateTourStatus.mutate(
          {
            tourStepIndex: currentStep,
            tourStartedAt,
            tourCompletedAt: isLastStep ? now : undefined,
          },
          {
            onSuccess: () => {
              prevStepRef.current = currentStep
              mutationInProgressRef.current = false
            },
            onError: () => {
              mutationInProgressRef.current = false
            },
          }
        )
      } else {
        prevStepRef.current = currentStep
      }
    }
  }, [currentStep, isTourCompleted, isOpen, user, updateTourStatus, totalSteps, needsOnboarding])

  React.useEffect(() => {
    if (wasOpenRef.current && !isOpen && !isTourCompleted) {
      if (skipRef.current) {
        skipRef.current = false
        wasOpenRef.current = isOpen
        return
      }

      const now = new Date().toISOString()
      const wasOnLastStep = prevStepRef.current !== null && prevStepRef.current >= totalSteps - 1
      const wasOnSetupStep = prevStepRef.current === 0 && needsOnboarding

      if (wasOnLastStep || !wasOnSetupStep) {
        const tourStartedAt = user?.tourStartedAt ? new Date(user.tourStartedAt).toISOString() : now

        updateTourStatus.mutate({
          tourStartedAt,
          tourCompletedAt: now,
        })
      }
    }
    wasOpenRef.current = isOpen
  }, [
    isOpen,
    isTourCompleted,
    updateTourStatus,
    totalSteps,
    needsOnboarding,
    user?.tourStartedAt,
    skipRef,
  ])

  return null
}
